import React, { useEffect, useState } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { useAuthStore } from '@/stores/authStore';
import type { NotificationPreferences } from '@/types';

const DEFAULT_PREFS: Omit<NotificationPreferences, 'id' | 'tenantId' | 'agentId'> = {
  emailEnabled: true,
  pushEnabled: true,
  inAppEnabled: true,
  slaWarningEnabled: true,
  slaBreachEnabled: true,
  escalationEnabled: true,
  systemEnabled: true,
};

/**
 * "My Notifications" — lets an agent toggle their own email notification
 * preferences. Consumes the already-existing GET/PUT
 * /api/notifications/{agentId}/preferences API (NotificationsController),
 * which had zero UI anywhere in the system before this page.
 *
 * Push/InApp toggles are intentionally NOT rendered — no push/in-app
 * transport exists yet to gate; only EmailEnabled currently gates a live
 * transport (the escalation-assigned "customer needs help" email).
 */
export const NotificationsSettingsPage: React.FC = () => {
  const agentId = useAuthStore((s) => s.user?.agentId);
  const [prefs, setPrefs] = useState<Omit<NotificationPreferences, 'id' | 'tenantId' | 'agentId'>>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!agentId) return;
    axiosClient
      .get<NotificationPreferences>(API.NOTIFICATIONS.PREFERENCES(agentId))
      .then(({ data }) => {
        setPrefs({
          emailEnabled: data.emailEnabled,
          pushEnabled: data.pushEnabled,
          inAppEnabled: data.inAppEnabled,
          slaWarningEnabled: data.slaWarningEnabled,
          slaBreachEnabled: data.slaBreachEnabled,
          escalationEnabled: data.escalationEnabled,
          systemEnabled: data.systemEnabled,
        });
      })
      .finally(() => setLoading(false));
  }, [agentId]);

  const toggle = (key: keyof typeof prefs) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!agentId) return;
    setSaving(true);
    try {
      await axiosClient.put(API.NOTIFICATIONS.PREFERENCES(agentId), prefs);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '1.5rem' }}>Loading…</div>;

  return (
    <div style={{ padding: '1.5rem', maxWidth: 480 }}>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem' }}>My Notifications</h1>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontWeight: 600 }}>
        <input type="checkbox" checked={prefs.emailEnabled} onChange={() => toggle('emailEnabled')} />
        Email notifications
      </label>

      <div style={{ marginLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={prefs.escalationEnabled}
            disabled={!prefs.emailEnabled}
            onChange={() => toggle('escalationEnabled')}
          />
          Customer needs help
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={prefs.slaWarningEnabled}
            disabled={!prefs.emailEnabled}
            onChange={() => toggle('slaWarningEnabled')}
          />
          SLA warning
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={prefs.slaBreachEnabled}
            disabled={!prefs.emailEnabled}
            onChange={() => toggle('slaBreachEnabled')}
          />
          SLA breach
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={prefs.systemEnabled}
            disabled={!prefs.emailEnabled}
            onChange={() => toggle('systemEnabled')}
          />
          System messages
        </label>
      </div>

      <button
        type="button"
        disabled={saving}
        onClick={handleSave}
        style={{ marginTop: '1.5rem', padding: '0.5rem 1.25rem', background: saving ? '#93c5fd' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
      {saved && (
        <span role="status" style={{ marginLeft: '0.75rem', color: '#166534', fontSize: '0.875rem' }}>Saved</span>
      )}
    </div>
  );
};

export default NotificationsSettingsPage;
